/**
 * UserInfoPanel — modal showing user info + recent messages + audit history,
 * plus quick mod actions (timeout / ban / unban). Mod-only.
 *
 * Open via setOpenUserId(internalUserId). The internal user id is the chattool
 * BIGINT user_id (string-cast) — surfaced by ChatMessage as data-user-id.
 *
 * Note: targetTwitchId is required for timeout/ban (Helix uses Twitch IDs).
 * We resolve it from the GET /api/users/:targetUserId/info response.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiFetch, type ApiError } from '../lib/api.js';
import { useModActions } from '../hooks/useModActions.js';

interface InfoResponse {
  user: {
    id: string;
    twitch_id: string | null;
    display_name: string;
    avatar_url: string | null;
    role: string | null;
    first_seen_at: string;
    last_seen_at: string | null;
  };
  streak: { streak_count: number; source: string; last_seen_at: string | null } | null;
  messages: Array<{
    id: string;
    source: string;
    external_id: string;
    username: string;
    content: string;
    is_deleted: boolean;
    created_at: string;
  }>;
  audit: Array<{
    id: string;
    mod_username: string;
    action: string;
    source: string;
    duration_seconds: number | null;
    reason: string | null;
    created_at: string;
  }>;
}

interface Props {
  /** User id to look up. Interpretation depends on `lookupBy`. */
  userId: string;
  /** "twitch" → resolve via users.twitch_id; "internal" → BIGINT users.id. */
  lookupBy?: 'twitch' | 'internal';
  onClose: () => void;
}

const QUICK_TIMEOUTS: Array<{ label: string; seconds: number }> = [
  { label: '10s', seconds: 10 },
  { label: '60s', seconds: 60 },
  { label: '5m', seconds: 300 },
  { label: '10m', seconds: 600 },
  { label: '1h', seconds: 3600 },
  { label: '24h', seconds: 86_400 },
];

export default function UserInfoPanel({ userId, lookupBy = 'internal', onClose }: Props) {
  const [info, setInfo] = useState<InfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const { timeoutUser, banUser, unbanUser } = useModActions();

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const qs = lookupBy === 'twitch' ? '?by=twitch' : '';
      const r = await apiFetch<InfoResponse>(
        `/api/users/${encodeURIComponent(userId)}/info${qs}`,
      );
      setInfo(r);
    } catch (err) {
      const apiErr = err as ApiError;
      setErrorMsg(`Fehler: ${apiErr?.status ?? '?'} ${apiErr?.message ?? 'unbekannt'}`);
    } finally {
      setLoading(false);
    }
  }, [userId, lookupBy]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runWithStatus(label: string, fn: () => Promise<void>): Promise<void> {
    setActionStatus(`${label}…`);
    try {
      await fn();
      setActionStatus(`${label}: ok`);
      // Reload audit list to reflect the new action
      await load();
    } catch (err) {
      const apiErr = err as ApiError;
      setActionStatus(`${label}: Fehler ${apiErr?.status ?? ''}`);
    }
  }

  const twitchId = info?.user.twitch_id || null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <h2 className="text-lg font-semibold">Benutzer-Info</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 text-sm"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>
        {loading && <div className="p-6 text-center text-slate-400">Lade…</div>}
        {errorMsg && <div className="p-4 text-sm text-red-400">{errorMsg}</div>}
        {info && (
          <div className="p-4 space-y-4">
            <header className="flex items-center gap-3">
              {info.user.avatar_url && (
                <img
                  src={info.user.avatar_url}
                  alt=""
                  className="w-12 h-12 rounded-full"
                  referrerPolicy="no-referrer"
                />
              )}
              <div>
                <div className="font-semibold text-base">{info.user.display_name}</div>
                <div className="text-xs text-slate-400">
                  Rolle: {info.user.role || 'viewer'} · Twitch-ID: {twitchId || '—'}
                </div>
                <div className="text-xs text-slate-500">
                  Erstmals gesehen: {new Date(info.user.first_seen_at).toLocaleString('de-DE')}
                </div>
                {info.streak && (
                  <div className="text-xs text-purple-300">
                    Sub-Streak: {info.streak.streak_count} Monate
                  </div>
                )}
              </div>
            </header>

            <section>
              <h3 className="text-sm font-semibold mb-2">Mod-Aktionen</h3>
              <div className="flex flex-wrap gap-1">
                {QUICK_TIMEOUTS.map((t) => (
                  <button
                    key={t.seconds}
                    type="button"
                    disabled={!twitchId}
                    onClick={() =>
                      twitchId &&
                      void runWithStatus(`Timeout ${t.label}`, () =>
                        timeoutUser(twitchId, t.seconds),
                      )
                    }
                    className="text-xs px-2 py-1 rounded bg-yellow-900/40 hover:bg-yellow-800/50 disabled:opacity-50"
                  >
                    {t.label}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={!twitchId}
                  onClick={() =>
                    twitchId && void runWithStatus('Ban', () => banUser(twitchId))
                  }
                  className="text-xs px-2 py-1 rounded bg-red-900/40 hover:bg-red-800/50 disabled:opacity-50"
                >
                  Ban
                </button>
                <button
                  type="button"
                  disabled={!twitchId}
                  onClick={() =>
                    twitchId && void runWithStatus('Unban', () => unbanUser(twitchId))
                  }
                  className="text-xs px-2 py-1 rounded bg-green-900/40 hover:bg-green-800/50 disabled:opacity-50"
                >
                  Unban
                </button>
              </div>
              {actionStatus && (
                <div className="mt-2 text-xs text-slate-300">{actionStatus}</div>
              )}
            </section>

            <section>
              <h3 className="text-sm font-semibold mb-2">
                Letzte Nachrichten ({info.messages.length})
              </h3>
              <div className="space-y-1 max-h-48 overflow-y-auto bg-slate-950/50 rounded p-2">
                {info.messages.length === 0 ? (
                  <div className="text-xs text-slate-500">Keine Nachrichten erfasst.</div>
                ) : (
                  info.messages.map((m) => (
                    <div
                      key={m.id}
                      className={`text-xs ${m.is_deleted ? 'opacity-50 line-through' : ''}`}
                    >
                      <span className="text-slate-500">
                        {new Date(m.created_at).toLocaleTimeString('de-DE')}
                      </span>{' '}
                      <span>{m.content}</span>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold mb-2">
                Audit-Historie ({info.audit.length})
              </h3>
              <div className="space-y-1 max-h-40 overflow-y-auto bg-slate-950/50 rounded p-2">
                {info.audit.length === 0 ? (
                  <div className="text-xs text-slate-500">Keine Mod-Aktionen.</div>
                ) : (
                  info.audit.map((a) => (
                    <div key={a.id} className="text-xs">
                      <span className="text-slate-500">
                        {new Date(a.created_at).toLocaleString('de-DE')}
                      </span>{' '}
                      <span className="text-purple-300">{a.action}</span>
                      {a.duration_seconds && <span> ({a.duration_seconds}s)</span>}{' '}
                      <span className="text-slate-400">von {a.mod_username}</span>
                      {a.reason && <span className="text-slate-500"> · {a.reason}</span>}
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
